import { Box, Typography } from "@mui/material"
import dayjs from "dayjs"
import type { FC } from "react"
import { useState } from "react"

import { PAY_FILE_TO_DATE_PAST_ERROR, PAY_FILE_TO_DATE_REQUIRED_ERROR } from "../constants"
import { useCreatePayFile } from "../hooks/useCreatePayFile"

import { PayFileGenerateConfirmationDialog } from "./PayFileGenerateConfirmationDialog"

import { Button } from "@/components/common/Button"
import { AerosDatePicker } from "@/components/UiKit"
import type { AerosDateValue } from "@/components/UiKit/AerosDatePicker/AerosDatePicker"
import { DATE_FORMAT } from "@/constants/formats"

interface PayFileCreateSectionProps {
  onSuccess?: () => void
}

export const PayFileCreateSection: FC<PayFileCreateSectionProps> = ({ onSuccess }) => {
  const [toDate, setToDate] = useState<AerosDateValue>(dayjs().format(DATE_FORMAT.ISO_18601))
  const [isConfirmOpen, setIsConfirmOpen] = useState(false)
  const { createPayFile, isCreating } = useCreatePayFile()

  // AK-5102: The date is required and may only be edited to a future date. `minDate` gates the
  // calendar grid only, so typed and cleared input need their own error state. Clearing the field
  // emits null, so the empty case has to be checked separately or the error would just vanish.
  const hasPastDate = Boolean(toDate) && dayjs(toDate).isBefore(dayjs(), "day")
  const hasDateError = !toDate || hasPastDate
  const dateErrorMessage = toDate ? PAY_FILE_TO_DATE_PAST_ERROR : PAY_FILE_TO_DATE_REQUIRED_ERROR

  const handleGenerate = () => {
    setIsConfirmOpen(true)
  }

  const handleGenerateConfirm = async () => {
    if (!toDate) return

    // The only editable input (the date) lives outside this dialog, so a failure isn't
    // retry-able without closing it first — keeping it open would just re-fire the same
    // failing request. Close on any resolution; the notification explains what happened.
    const isSuccess = await createPayFile({ toDate })
    setIsConfirmOpen(false)
    if (isSuccess) onSuccess?.()
  }

  return (
    <>
      <Box className='flex flex-col gap-6 rounded-md border border-gray-300 p-6'>
        <Box className='flex items-center gap-8'>
          <Typography variant='subtitle1' className='text-ml font-bold text-gray-900'>
            Create Pay File
          </Typography>
          <Box className='flex flex-1 items-center gap-6'>
            <Box className='flex items-center gap-2'>
              <Typography className='whitespace-nowrap text-sm text-gray-900'>
                To Scheduled Pay Date of
                <span aria-hidden className='ml-1 text-secondary-600'>
                  *
                </span>
              </Typography>
              <AerosDatePicker
                required
                value={toDate}
                onChange={setToDate}
                minDate={dayjs()}
                error={hasDateError}
                helperText={hasDateError ? dateErrorMessage : undefined}
                className='w-[200px]'
                data-testid='pay-file.create-section.to-date-field'
              />
            </Box>
            <Button
              appearance='contained'
              rootElementName='button'
              disabled={hasDateError || isCreating}
              onClick={handleGenerate}
              data-testid='pay-file.create-section.generate-button'
            >
              Generate Pay File
            </Button>
            <Typography className='text-xs text-gray-700'>
              The pay file will include all invoices with a Scheduled Pay date prior to or equal to the selected date.
            </Typography>
          </Box>
        </Box>
      </Box>
      {isConfirmOpen && toDate !== null && (
        <PayFileGenerateConfirmationDialog
          confirmDisabled={isCreating}
          toDate={toDate}
          onClose={() => setIsConfirmOpen(false)}
          onConfirm={handleGenerateConfirm}
        />
      )}
    </>
  )
}
